<template>
  <div class="auth-card-wrapper">
    <div class="auth-card">
      <div class="auth-card-container">
        <img
          class="logo"
          alt="logo"
          src="@/assets/nubuilder.svg"
        />
        <template v-if="$route.query.thanks">
          <h3 class="text-h5 font-weight-bold pb-4 text-center">Your account is now enabled for login.</h3>
          <p class="text-center">
            Please contact us at
            <a href="mailto:support@qlerify.com">support@qlerify.com</a> with
            <br/>any questions, suggestions or feedback!
          </p>
        </template>
        <template v-else-if="$route.query.resetPasswordSuccess">
          <h3 class="text-h5 font-weight-bold pa-3 mb-1 bg-green-lighten-1 rounded text-center">Your password has been
            reset!</h3>
          <p class="text-center">
            You can now login with your new password.
          </p>
        </template>

        <template v-else>
          <h3 class="text-title">Login to your account</h3>
        </template>
      </div>

      <v-form ref="form" v-model="valid">
        <div class="auth-card-container form-fields" style="margin-bottom: 8px">
          <v-text-field
            name="email"
            label="Email Address"
            type="email"
            ref="username"
            v-model="email"
            :rules="[rules.required, rules.email]"
            v-on:keyup.enter="submit"
            tabindex="1"
            autocomplete="username"
            variant="outlined"
            density="default"
            data-cy="email-input"
          ></v-text-field>

          <v-text-field
            name="password"
            label="Password"
            id="password"
            ref="password"
            v-model="password"
            :append-inner-icon="e1 ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
            @click:append-inner="() => (e1 = !e1)"
            :type="e1 ? 'password' : 'text'"
            min="8"
            :rules="[rules.required]"
            counter
            v-on:keyup.enter="submit"
            tabindex="2"
            autocomplete="current-password"
            variant="outlined"
            density="default"
            data-cy="password-input"
          ></v-text-field>

          <p class="message bg-error" v-html="error" v-if="error" data-cy="login-error"/>
          <p class="message bg-indigo" v-html="message" v-if="message"/>
        </div>

        <div class="auth-card-container">
          <div class="auth-card-footer justify-space-between">
            <div class="auth-card-footer-part auth-card-footer-part_column">
              <router-link :to="{ path: '/reset-password' }" class="text text-decoration-none">
                Forgot password?
              </router-link>
              <router-link :to="{ path: '/confirm' }" class="text text-decoration-none">
                Got a registration code?
              </router-link>
            </div>
            <div class="auth-card-footer-part">
              <p class="text">
                No account?
                <router-link
                  class="text text-decoration-none"
                  tabindex="5"
                  :to="{ path: '/signup', query: { projectId: $route.query.projectId || undefined }  }"
                >
                  <span class="light--text">Sign up</span>
                </router-link>
              </p>
              <v-btn rounded="lg" size="large" color="#363a40" class="ml-auto d-flex" variant="flat" @click="submit"
                     tabindex="3">
                Log in
              </v-btn>
            </div>
          </div>
        </div>
      </v-form>

      <div class="auth-card-container">
        <text-divider text="or"/>
        <google-sign-in-button/>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { authHelpers, parseAuthCustomState } from '@/api/authHelpers'
import GoogleSignInButton from '@/components/GoogleSignInButton.vue'
import TextDivider from '@/components/TextDivider.vue'
import { Auth } from '@aws-amplify/auth'

export default {
  components: {
    TextDivider,
    GoogleSignInButton
  },
  data () {
    return {
      valid: true,
      email: '',
      password: '',
      error: null,
      message: null,
      rules: {
        required: (value) => !!value || 'Required.',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        }
      },
      e1: true
    }
  },
  created () {
    if (this.$route.query.username !== '') {
      this.email = this.$route.query.username
    }
  },
  async mounted () {
    if (this.$route.query.thanks) {
      // window.gtag('event', 'conversion', { send_to: 'AW-676520547/ZI1ACJashMwBEOPEy8IC' })
    }
    if (this.$route.query.username) {
      this.$refs.password.focus()
    }
    if (Auth && this.$route.query.googleSignIn) {
      try {
        const currentUser = await Auth.currentAuthenticatedUser()
        if (currentUser) {
          try {
            this.$loadingState.start()

            const sessionIdInfo = currentUser.signInUserSession.idToken.payload
            await this.initializeSession(sessionIdInfo)
          } catch (err) {
            this.$loadingState.stop()
            this.error = err.message.replace('GraphQL error: ', '')
            authHelpers.logoutAndClearStore()
          }
        }
      } catch (e) {
        console.log(e)
      }
    }
  },
  methods: {
    async updateUser () {
      const session = await this.$cognitoAuth.getSession()
      const sessionIdInfo = session.getIdToken().payload
      const currentUser = await Auth.currentAuthenticatedUser()

      if (sessionIdInfo?.identities?.[0]?.providerName === 'GLHOMES') {
        await Auth.updateUserAttributes(currentUser, {
          'custom:organisation': 'GL Homes'
        })
      }

      const attributes = await this.$cognitoAuth.getUserAttributes()
      const indexCompany = attributes.findIndex(item => item.Name === 'custom:organisation')
      const company = indexCompany >= 0 ? attributes[indexCompany].Value : null
      const indexReferralSource = attributes.findIndex(item => item.Name === 'custom:referral_source')
      const referralSource = indexReferralSource >= 0 ? attributes[indexReferralSource].Value : null
      const indexUsageIntent = attributes.findIndex(item => item.Name === 'custom:usage_intent')
      const usageIntent = indexUsageIntent >= 0 ? attributes[indexUsageIntent].Value : null
      const indexFirstname = attributes.findIndex(item => item.Name === 'given_name')
      const firstname = attributes[indexFirstname].Value
      const indexLastname = attributes.findIndex(item => item.Name === 'family_name')
      const lastname = attributes[indexLastname].Value
      const indexEmail = attributes.findIndex(item => item.Name === 'email')
      const email = attributes[indexEmail].Value
      const indexPhoneNumber = attributes.findIndex(item => item.Name === 'phone_number')
      const phoneNumber = indexPhoneNumber >= 0 ? attributes[indexPhoneNumber].Value : null
      const queryResult = await store.dispatch('user/getCurrentUser')
      const variables = {
        firstname,
        lastname,
        email,
        company,
        referralSource,
        usageIntent,
        phoneNumber,
        increaseLoginCounter: true
      }
      if (queryResult.data.getCurrentUser) {
        await store.dispatch('user/updateUser', variables)
      } else {
        await store.dispatch('user/createUser', variables)
        window.gtag_report_conversion()
        if (company) {
          await store.dispatch('organisations/createOrganisation', { name: company })
        }
        await store.dispatch('teamMembers/convertInvitesToTeamMembers')
        this.$store.dispatch('projectMemberships/listProjectMembershipsForCurrentUser')
      }
    },
    async submit () {
      const { valid } = await this.$refs.form.validate()
      if (valid) {
        try {
          this.$loadingState.start()
          await this.$cognitoAuth.signin(this.email, this.password)

          const session = await this.$cognitoAuth.getSession()
          const sessionIdInfo = session.getIdToken().payload

          await this.initializeSession(sessionIdInfo)
        } catch (err) {
          console.log(err)
          this.$loadingState.stop()
          this.error = err.message.replace('GraphQL error: ', '')
          authHelpers.logoutAndClearStore()
        }
      }
    },
    async initializeSession (sessionIdInfo) {
      let adminUser = false
      if (sessionIdInfo['cognito:groups']) {
        if (sessionIdInfo['cognito:groups'][0] === 'Admin') {
          adminUser = true
        }
      }

      const state = parseAuthCustomState(this.$route.query.state)
      const projectId = this.$route.query.projectId || state?.projectId || null

      store.commit('login', { email: sessionIdInfo.email, adminUser, userId: sessionIdInfo['cognito:username'] })
      await this.updateUser()
      this.$loadingState.stop()
      const path = `/dashboard/${projectId || ''}`
      await this.$router.push({ path })
    }
  }
}
</script>

<style scoped>
.text {
  color: #1D1F22;
  font-size: 13px;
  text-align: left;
  margin-bottom: 0;
  line-height: 14.3px;
}

.text-decoration-none {
  text-decoration: none;
}

.logo {
  width: 110px;
  margin-top: 9px;
  margin-bottom: 30px;
}

.form-fields {
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
</style>
